import React, { useEffect, useMemo, useState } from 'react';
import { Agreement, AGREEMENT_SUB_TYPE, AgreementSubType } from '@/apis/agreement/interface';
import ButtonComponent from '@/components/ButtonComponent';
import TextComponent from '@/components/TextComponent';
import { LOG_EVENT } from '@/constants/logs';
import useAgreementQuery from '@/hooks/agreement/useAgreementQuery';
import { getPageName, reportLog } from '@/lib/logger';
import { authState } from '@/recoil/auth';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import ScrollSelectWrapper from './containers/ScrollSelectWrapper';
import theme from '@/styles/theme';

const getAgreementCheckDescription = (agreementSubType: AgreementSubType) => {
  switch (agreementSubType) {
    case AGREEMENT_SUB_TYPE.TERMS_AND_CONDITIONS:
      return '약국은 위 내용에 대한 충분한 설명을 받고 이를 모두 확인하였으며, 본 약관에 서명 날인함으로써 이의 없이 해당 내용에 동의합니다.';
    case AGREEMENT_SUB_TYPE.CONSIGNMENT_CONTRACT:
      return '수탁자는 위 내용에 대한 충분한 설명을 받고 이를 모두 확인하였으며, 본 약관에 서명 날인함으로써 이의 없이 해당 내용에 동의합니다.';
  }
};

const TermAgreePage = () => {
  const { devices } = useRecoilValue(authState);
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const { getAgreements, updateAgreementChecked } = useAgreementQuery();
  const { data: agreementsResponse } = getAgreements({
    agreementType: 'PHARMACIST_TERMS_AGREEMENT'
  });

  const buttonDisabled = useMemo(
    () => agreements.some(agreement => !agreement.isChecked),
    [agreements]
  );

  const checkAgreement = (id: number) => {
    setAgreements(agreements =>
      agreements.map(agreement => {
        if (agreement.id === id)
          return { ...agreement, isChecked: !agreement.isChecked };
        return { ...agreement };
      })
    );
  };

  const onSubmit = async () => {
    reportLog(`${LOG_EVENT.SUBMIT_AGREEMENT}`, {
      event_name: LOG_EVENT.SUBMIT_AGREEMENT,
      page_name: `${getPageName(window.location.pathname)}`,
      pharmacistId: devices?.length
        ? devices[devices?.length - 1]?.pharmacistId
        : 0
    });

    updateAgreementChecked.mutate({
      agreementType: 'PHARMACIST_TERMS_AGREEMENT',
      approvedAgreementIds: agreements
        .filter(agreement => agreement.isChecked)
        .map(agreement => agreement.id),
      disapprovedAgreementIds: []
    });
  };

  useEffect(() => {
    if (agreementsResponse) setAgreements(agreementsResponse);
  }, [agreementsResponse]);

  return (
    <Wrapper>
      <BackGroundWrapper>
        <FormWrapper>
          <Title>
            <TextComponent fontSize={30} fontWeight={800}>
              닥터나우 약관 동의
            </TextComponent>
          </Title>
          <Content>
            {agreements.map((agreement: Agreement) => (
              <ScrollSelectWrapper
                key={agreement.id}
                agreement={agreement}
                description={getAgreementCheckDescription(
                  agreement.agreementSubType
                )}
                onClick={() => checkAgreement(agreement.id)}
              />
            ))}
          </Content>
        </FormWrapper>
        <ButtonWrapper>
          <ButtonComponent
            style={{ width: '340px' }}
            textColor={theme.DF_COLOR.WHITE}
            text={
              buttonDisabled
                ? '모든 약관을 확인 후 동의해주세요.'
                : '모든 약관에 동의했습니다.'
            }
            onClick={onSubmit}
            disabled={buttonDisabled}
            bgColor={theme.DF_COLOR.GREEN500}
          />
        </ButtonWrapper>
      </BackGroundWrapper>
    </Wrapper>
  );
};

export default TermAgreePage;

const Wrapper = styled.div`
  margin-top: 2vh;
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
`;

const BackGroundWrapper = styled.div`
  width: 100%;
  height: 1141px;
  padding: 2% 0 3% 0;
  display: flex;
  flex-flow: column;
  align-items: center;
  background: ${theme.DF_COLOR.WHITE};
`;

const FormWrapper = styled.div`
  height: 100%;
  width: 80%;
  position: relative;
  display: flex;
  flex: 0.85;
  flex-flow: column;
  align-items: center;
`;

const Title = styled.div`
  flex: 0.1;
  margin-bottom: 13px;
  ${({ theme }) => theme.media.half`
    flex-flow: column;
  `};
`;

const Content = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;
  justify-content: center;
  flex: 0.7;
`;

const ButtonWrapper = styled.div`
  width: 80%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 0.1;
`;
